import { useTranslation } from "react-i18next";
import moment from "moment";
import { TeambookReactSelect } from "../../../../default_components/TeambookReactSelect";
import { InputNumber } from "../../../../default_components/InputNumber";
import { useEffect } from "react";
import PropTypes from "prop-types";
import TeambookRadioButton from "../../../../default_components/TeambookRadioButton";
import TeambookDatePicker from "../../../../default_components/teambook_date_selector/DatePicker";
import { DateTime } from "luxon";
import { icons } from "../../../../default_images/IconsList";
import { TeambookIcon, ICON_COLOR } from "../../../../default_images/TeambookIcon";
import { TeambookCheckbox } from "../../../../default_components/TeambookCheckbox";
import {
  useBookingModalStore,
  useBookingsEditingStore,
  useDateStore,
  useZoomStore,
} from "../../../../../stores/planner";
import { shallow } from "zustand/shallow";

const BookingRepetition = ({
  creationBookingsArray,
  booking,
  deleteRepetitionClick,
  canDeleteRepetition,
  createWindowOpened,
  createBookingsRef,
}) => {
  const { t, i18n } = useTranslation();
  moment.locale(i18n.language);
  const modalType = createWindowOpened ? "creating" : "editing";

  const [repetition, setRepetition, repetitionComponentOpen, setRepetitionComponentOpen] = useBookingModalStore(
    (state) => [state.repetition, state.setRepetition, state.repetitionComponentOpen, state.setRepetitionComponentOpen],
    shallow
  );
  const [zoom] = useZoomStore((state) => [state.zoom], shallow);
  const [date] = useDateStore((state) => [state.date], shallow);
  const [editingBookings] = useBookingsEditingStore((state) => [state.bookingsEditing], shallow);

  useEffect(() => {
    if (createWindowOpened) {
      if (zoom === 1) {
        setRepetition({ endsAtDate: DateTime.fromJSDate(date).plus({ months: 2 }).toJSDate() });
      } else {
        setRepetition({
          endsAtDate:
            DateTime.fromJSDate(
              createBookingsRef?.current?.find((ref) => ref?.bookings?.length > 0)?.bookings?.[0]?.[1]
            )
              .plus({ months: 2 })
              .toJSDate() || DateTime.now().plus({ months: 2 }).toJSDate(),
        });
      }
    }
  }, [createWindowOpened]);

  useEffect(() => {
    if (
      [...new Set(creationBookingsArray.map((data) => data[0]))].length !== creationBookingsArray.length &&
      repetition.type === "daily"
    ) {
      setRepetition({ type: "weekly" });
    }
  }, [creationBookingsArray]);

  const translateKind = (repetition_kind) => {
    return t(`planning.${repetition_kind}`).toLowerCase();
  };

  const weekdayWithCapital = (date) => {
    return moment(date)
      .format("dddd")
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  const repetitionEveryHash = () => {
    //1, 2, 3, ..., 30, 31
    let repetitions = Array.from({ length: 31 }, (_, i) => i + 1);

    if (repetition.type === "weekly") {
      repetitions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
    } else if (repetition.type === "monthly") {
      repetitions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    } else if (repetition.type === "yearly") {
      repetitions = [1, 2, 3, 4, 5];
    } else if (repetition.type === "custom") {
      repetitions = [1, 2, 3, 4];
    }

    return repetitions.map(function (el) {
      return {
        value: el,
        label: el,
        name: el,
      };
    });
  };

  const repetitionOptions = () => {
    const values = [
      {
        value: "weekly",
        label: t("planning.4_weeks"),
      },
      {
        value: "monthly",
        label: t("planning.3_months"),
      },
      {
        value: "yearly",
        label: t("planning.years"),
      },
    ];

    if ([...new Set(creationBookingsArray.map((data) => data[0]))].length === creationBookingsArray.length) {
      values.unshift({
        value: "daily",
        label: t("planning.days"),
      });
    }

    return values;
  };

  const allowBookingRepetition = () => {
    if (modalType === "editing") {
      return editingBookings.length === 1;
    } else {
      const userDates = {};
      let result = true;

      creationBookingsArray.forEach((booking) => {
        userDates[booking[0]] ||= [];

        userDates[booking[0]].push(DateTime.fromJSDate(booking[1]).weekNumber);
      });

      Object.values(userDates).forEach((weekNumbers) => {
        if (weekNumbers.reduce((ps, a) => ps + a, 0) / weekNumbers[0] !== weekNumbers.length) {
          result = false;
        }
      });

      return result;
    }
  };

  if (!allowBookingRepetition()) {
    return null;
  }

  return (
    <>
      <div className="row checkbox">
        <div style={{ display: "flex", alignItems: "center" }}>
          <TeambookCheckbox
            checked={repetitionComponentOpen}
            onChange={() => {
              setRepetition({ type: repetitionComponentOpen ? null : "daily" });
              setRepetitionComponentOpen(!repetitionComponentOpen);
            }}
          />
          <p
            onClick={() => {
              setRepetition({ type: repetitionComponentOpen ? null : "daily" });
              setRepetitionComponentOpen(!repetitionComponentOpen);
            }}
            className="booking-window__data-element-text"
          >
            {t("planning.repetition")}
          </p>
        </div>
      </div>

      {repetitionComponentOpen && (
        <div className="booking-window__grouped-data-element">
          <div className="booking-window__repetition-container">
            <div className="booking-window__repetition-container__header">
              <p>{t("planning.every")}:</p>
            </div>

            <div className="booking-window__repetition-row">
              <TeambookRadioButton
                fieldName={""}
                checked={repetition.type !== "custom"}
                onCheck={() => setRepetition({ type: "daily" })}
                options={repetitionOptions()}
              />

              <InputNumber
                value={repetition.every.value}
                setValue={(value) => setRepetition({ every: { value: value, label: value } })}
                min={1}
                max={31}
                step={1}
                showDropdown
                dropdownOptions={repetitionEveryHash()}
                arrowSize={16}
              />

              <TeambookReactSelect
                fieldName={""}
                value={repetitionOptions().find((option) => option.value === repetition.type)}
                onChange={(value) => setRepetition({ type: value.value })}
                menuPlacement={"top"}
                isSearchable={false}
                options={repetitionOptions()}
                width={"100%"}
                minWidth={"100%"}
                height={32}
                style={{ width: "100%" }}
              />
            </div>

            <div className="booking-window__repetition-row">
              <TeambookRadioButton
                checked={repetition.type === "custom"}
                onCheck={() => setRepetition({ type: "custom" })}
              />
              <p>
                {`${Math.ceil(creationBookingsArray[0][1].getDate() / 7)} ${weekdayWithCapital(
                  creationBookingsArray[0][1]
                )} ${t("planning.of_month")}`}
              </p>
            </div>

            <div className="booking-window__repetition-container__header" style={{ marginTop: 20 }}>
              <p>{t("planning.will_end")}</p>
            </div>

            <div className="booking-window__repetition-row">
              <div className="booking-window__repetition-end-switch">
                <div
                  className={`booking-window__repetition-end-switch__option ${repetition.endsAfter ? "active" : ""}`}
                  onClick={() => setRepetition({ endsAfter: true, endsAt: false })}
                >
                  <p>{t("planning.repetition_after")}</p>
                </div>
                <div
                  className={`booking-window__repetition-end-switch__option ${repetition.endsAt ? "active" : ""}`}
                  onClick={() => setRepetition({ endsAfter: false, endsAt: true })}
                >
                  <p>{t("planning.repetition_on")}</p>
                </div>
              </div>

              {repetition.endsAfter && (
                <div className="booking-window__repetition-row__input-number">
                  <InputNumber
                    value={repetition.endsAfterNumber}
                    setValue={(value) => setRepetition({ endsAfterNumber: value })}
                    min={0}
                    max={100}
                    step={1}
                    arrowSize={16}
                  />

                  <p>{t("planning.times")}</p>
                </div>
              )}

              {repetition.endsAt && (
                <div className="booking-window__repetition-row__input-number">
                  <TeambookDatePicker
                    variant="borderedInline"
                    height={32}
                    formatStart="d/MM/y"
                    from={DateTime.fromJSDate(repetition.endsAtDate)}
                    onDayClick={(date) => setRepetition({ endsAtDate: date.toJSDate() })}
                    type="default"
                    highlight="selected"
                    closeOnSelect={true}
                    width={130}
                    range={1}
                    disabledRanges={["1_year"]}
                    customIcon={
                      <TeambookIcon name={icons.CALENDAR} color={ICON_COLOR.DEFAULT} style={{ marginLeft: 8 }} />
                    }
                    menuPosition="left"
                    style={{ borderRadius: 4 }}
                  />
                </div>
              )}
            </div>

            {repetition.type !== "custom" && (
              <div className="booking-window__repetition-ignore-weekends">
                <TeambookCheckbox
                  checked={repetition.ignoreWeekends}
                  onChange={() => setRepetition({ ignoreWeekends: !repetition.ignoreWeekends })}
                  color="primary"
                />
                <p
                  className="ignore-weekends"
                  onClick={() => setRepetition({ ignoreWeekends: !repetition.ignoreWeekends })}
                >
                  {t("planning.ignore_weekends")}
                </p>
              </div>
            )}

            {booking?.repetition?.[0] && (booking.repetition[0].last_booking_at || booking.repetition[0].ends_on) && (
              <div className="booking-reccuring__container">
                <p className="booking-repetition-text" lang={i18n.language}>
                  {t("planning.repetition_indicator", {
                    kind: translateKind(booking.repetition[0].kind),
                    range: booking.repetition[0].last_booking_at || booking.repetition[0].ends_on,
                  })}
                </p>
                {canDeleteRepetition && (
                  <TeambookIcon
                    name={icons.TRASH}
                    tooltipTitle={t("planning.delete_repetition")}
                    onClick={deleteRepetitionClick}
                    style={{ width: 20, height: 20 }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

BookingRepetition.propTypes = {
  creationBookingsArray: PropTypes.array.isRequired,
  booking: PropTypes.object.isRequired,
  deleteRepetitionClick: PropTypes.func.isRequired,
  canDeleteRepetition: PropTypes.bool.isRequired,
  createWindowOpened: PropTypes.bool.isRequired,
  createBookingsRef: PropTypes.object.isRequired,
};

export default BookingRepetition;
